import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useDiscoveryPrice, DiscoveryHookModal, trackDiscovery } from '../components/DiscoveryHook';

const PROTOCOLS = [
  { num: 1, title: 'Subject Test', q: 'Who is the text talking about?', detail: 'Identify every noun, pronoun, and named entity. Lock in who the passage addresses.' },
  { num: 2, title: 'Verb Test', q: 'What does the text say happens?', detail: 'Examine every verb — tense, mood, voice. What action does the grammar describe?' },
  { num: 3, title: 'Timeline Test', q: 'When does this take place?', detail: 'Look for temporal markers — before, after, until, when. Sequence the events grammatically.' },
  { num: 4, title: 'Location Test', q: 'Where does the action happen?', detail: 'Identify geographical and spatial references. Where does the text place the events?' },
  { num: 5, title: 'Purpose Test', q: 'Why does this happen?', detail: 'Find causal conjunctions — because, so that, in order that. What reason does the text give?' },
  { num: 6, title: 'Method Test', q: 'How does it occur?', detail: 'Trace the mechanism the text describes. By what means does the action take place?' },
];

const SAMPLE_PASSAGES = [
  '2 Thessalonians 2:1-3',
  'Matthew 24:29-31',
  '1 Corinthians 15:51-52',
  'Revelation 20:4-6',
  '1 Thessalonians 5:9',
  'Daniel 9:27',
];

export default function GITExplorer() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [passage, setPassage] = useState('');
  const [showHook, setShowHook] = useState(false);
  const [activeProtocol, setActiveProtocol] = useState(null);
  const discovery = useDiscoveryPrice();
  const isGuest = !isAuthenticated();

  const handleInvestigate = () => {
    if (!passage.trim()) return;
    if (isGuest) {
      setShowHook(true);
    } else {
      navigate(`/git-investigator?passage=${encodeURIComponent(passage)}`);
    }
  };

  const handleProtocolClick = (idx) => {
    setActiveProtocol(activeProtocol === idx ? null : idx);
    if (isGuest && activeProtocol !== idx) {
      // After exploring 2+ protocols, trigger the hook
      if (idx >= 2) {
        discovery.recordExploration();
      }
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#060503' }}>
      {/* Nav */}
      <nav className="relative z-20 flex items-center justify-between px-6 py-4 border-b border-amber-900/20 backdrop-blur-sm bg-[#060503]/50">
        <button onClick={() => navigate('/')} className="flex items-center gap-2 text-amber-500 hover:text-amber-300 transition-colors text-sm font-serif" data-testid="btn-git-back-home">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
          Treasure Map
        </button>
        <h1 className="text-base font-serif tracking-[0.35em] text-amber-600/70 uppercase">GIT Protocol</h1>
        <div>
          {isGuest ? (
            <button onClick={() => navigate('/login')} className="px-3 py-1.5 bg-amber-600/20 border border-amber-700/40 rounded-lg text-amber-400 hover:bg-amber-600/30 transition-colors text-sm font-medium" data-testid="btn-git-login">
              Sign In
            </button>
          ) : (
            <button onClick={() => navigate('/git-investigator')} className="px-3 py-1.5 bg-amber-600 rounded-lg text-black text-sm font-semibold hover:bg-amber-500 transition-colors" data-testid="btn-full-investigator">
              Full Investigator
            </button>
          )}
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 py-12">
        {/* Hero */}
        <div className="text-center mb-16">
          <div className="text-[9px] tracking-[0.4em] uppercase font-mono text-amber-400/70 mb-3">
            A Six-Protocol System
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold mb-4" style={{
            background: 'linear-gradient(180deg, #fcd34d 0%, #d97706 50%, #92400e 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          }}>
            The Grammar Integrity Test
          </h1>
          <p className="text-lg text-amber-300/60 font-serif max-w-2xl mx-auto leading-relaxed">
            Apply plain grammatical analysis to any biblical passage, theological claim, or doctrinal position. No speculation. No tradition. Just the text.
          </p>
        </div>

        {/* 6 Protocols */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-16">
          {PROTOCOLS.map((p, idx) => (
            <button
              key={p.num}
              onClick={() => handleProtocolClick(idx)}
              data-testid={`protocol-${p.num}`}
              className={`text-left p-5 rounded-xl border transition-all duration-300 ${
                activeProtocol === idx
                  ? 'bg-amber-900/15 border-amber-700/40 scale-[1.02]'
                  : 'bg-[#0f0a04]/60 border-amber-900/10 hover:border-amber-800/30'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold font-serif shrink-0 transition-colors ${
                  activeProtocol === idx ? 'bg-amber-600 text-black' : 'bg-amber-900/20 text-amber-500/70'
                }`}>
                  {p.num}
                </div>
                <div>
                  <h3 className="text-amber-200/90 font-serif font-semibold text-sm">{p.title}</h3>
                  <p className="text-amber-400/60 text-xs mt-0.5 italic">{p.q}</p>
                  {activeProtocol === idx && (
                    <p className="text-amber-300/50 text-xs mt-2 leading-relaxed">{p.detail}</p>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Trial Investigator */}
        <div className="max-w-2xl mx-auto">
          <div className="rounded-2xl border p-8" style={{ background: 'rgba(15,10,5,0.7)', borderColor: 'rgba(120,80,20,0.15)' }}>
            <div className="text-center mb-6">
              <div className="text-[9px] tracking-[0.3em] uppercase font-mono text-amber-400/60 mb-2">
                {isGuest ? 'Try It' : 'Investigate'}
              </div>
              <h2 className="text-2xl font-serif text-amber-200/80">
                {isGuest ? 'Test the GIT Investigator' : 'Start an Investigation'}
              </h2>
              <p className="text-amber-400/55 text-sm mt-1">
                Enter any biblical passage and let the AI apply all six protocols
              </p>
            </div>

            {/* Passage Input */}
            <div className="mb-4">
              <input
                type="text"
                value={passage}
                onChange={e => setPassage(e.target.value)}
                placeholder="e.g., Matthew 24:29-31"
                data-testid="git-trial-input"
                className="w-full px-4 py-3 rounded-lg border text-sm font-serif text-amber-100 placeholder:text-amber-600/40 focus:outline-none focus:border-amber-600/40"
                style={{ background: 'rgba(10,8,4,0.6)', borderColor: 'rgba(120,80,20,0.2)' }}
                onKeyDown={e => e.key === 'Enter' && handleInvestigate()}
              />
            </div>

            {/* Quick picks */}
            <div className="flex flex-wrap gap-2 mb-6">
              {SAMPLE_PASSAGES.map(p => (
                <button
                  key={p}
                  onClick={() => setPassage(p)}
                  data-testid={`sample-${p.replace(/\s+/g, '-')}`}
                  className={`px-3 py-1 rounded-full text-xs font-mono transition-all ${
                    passage === p
                      ? 'bg-amber-600/20 text-amber-400 border border-amber-600/40'
                      : 'bg-amber-900/10 text-amber-500/60 border border-amber-900/10 hover:text-amber-400'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>

            {/* Investigate Button */}
            <button
              onClick={handleInvestigate}
              disabled={!passage.trim()}
              data-testid="git-trial-investigate-btn"
              className="w-full py-4 rounded-xl font-semibold text-base transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-30 disabled:cursor-not-allowed"
              style={{
                background: passage.trim() ? 'linear-gradient(135deg, #d97706, #b45309)' : 'rgba(120,80,20,0.15)',
                color: passage.trim() ? '#000' : '#5a3e14',
                boxShadow: passage.trim() ? '0 4px 20px rgba(217,119,6,0.3)' : 'none',
              }}
            >
              {isGuest ? 'Investigate This Passage' : 'Open in GIT Investigator'}
            </button>

            {isGuest && (
              <p className="text-center text-[10px] text-amber-600/50 mt-3 font-mono">
                AI-powered analysis using all 6 GIT protocols
              </p>
            )}
          </div>
        </div>

        {/* Bottom CTA for guests */}
        {isGuest && (
          <div className="text-center mt-16 pb-8">
            <p className="text-amber-400/50 text-sm font-serif mb-4">
              The GIT Investigator is available with your account
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate('/buy')}
                className="px-6 py-3 rounded-xl text-sm font-semibold bg-amber-600 text-black hover:bg-amber-500 transition-colors"
                data-testid="git-cta-buy"
              >
                Get the Book
              </button>
              <button
                onClick={() => navigate('/register')}
                className="px-6 py-3 rounded-xl text-sm font-medium border border-amber-800/30 text-amber-500 hover:bg-amber-900/20 transition-colors"
                data-testid="git-cta-signup"
              >
                Start Free as Seeker
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Discovery Hook Modal */}
      <DiscoveryHookModal
        isOpen={showHook && isGuest}
        onClose={() => { discovery.recordExploration(); setShowHook(false); }}
        currentPrice={discovery.currentPrice}
        nextPrice={discovery.nextPrice}
        savings={discovery.savings}
        retailPrice={discovery.retailPrice}
        isMaxPrice={discovery.isMaxPrice}
        piecesExplored={discovery.piecesExplored}
        sourcePage="git_explorer"
        pieceTitle={passage || null}
        onBuy={() => { setShowHook(false); navigate('/buy'); }}
        onSignup={() => { discovery.recordExploration(); setShowHook(false); navigate('/register'); }}
      />
    </div>
  );
}
